import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Search, 
  Mail, 
  Users, 
  QrCode, 
  Settings, 
  CreditCard, 
  Building2,
  CheckCircle,
  XCircle,
  ExternalLink,
  Copy,
  Check,
  Loader2,
  ChevronRight,
  ChevronDown,
  DollarSign,
  FileText
} from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { Button } from '../../components/ui/Button';
import { useAdminCompanyUsers } from '../../hooks/useAdminCompanyUsers';
import { useAdminDraftStudents } from '../../hooks/useAdminDraftStudents';
import { useUpdateUserRole } from '../../hooks/useUpdateUserRole';
import { useSystemRoles } from '../../hooks/useSystemRoles';

interface Company {
  id: string;
  name: string | null;
  company_email: string | null;
  contact_fn: string | null;
  contact_ln: string | null;
  phone: string | null;
  city: string | null;
  state: string | null;
  is_active: boolean;
  setup_complete: boolean;
  is_internal?: boolean; // Optional until migration is created
  enable_bulk_draft_updates?: boolean; // Optional until migration is created
  enable_billing?: boolean; // Feature toggle for billing
  created_at: string;
  signup_qr_url: string | null;
  stripe_connect_account_id: string | null;
  stripe_connect_account_status: string | null;
  billing_status?: 'rollops' | 'self_managed' | 'not_configured';
}


interface CompanyLocation {
  id: string;
  name: string | null;
  address: string | null;
  city: string | null;
  state: string | null;
}

// Billing Settings Modal Component
function BillingSettingsModal({ 
  company, 
  formData, 
  onClose, 
  onSave, 
  onFormChange 
}: { 
  company: Company | null; 
  formData: { payment_provider: 'rollops' | 'self-managed'; billing_strategy: 'monthly_fixed_date' | 'on_signup'; fixed_billing_day: number }; 
  onClose: () => void; 
  onSave: () => void; 
  onFormChange: (data: { payment_provider: 'rollops' | 'self-managed'; billing_strategy: 'monthly_fixed_date' | 'on_signup'; fixed_billing_day: number }) => void;
}) {
  if (!company) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-[#221F1F] border border-[#504A4A] rounded-lg p-6 max-w-md w-full mx-4">
        <h3 className="text-xl font-semibold text-[#EBFFEE] mb-4">
          Edit Billing Settings - {company.name || 'Unknown Company'}
        </h3>
        
        <div className="space-y-4">
          <div>
            <label className="block text-sm text-[#A9A3A3] mb-2">Payment Provider</label>
            <select
              value={formData.payment_provider}
              onChange={(e) => onFormChange({ ...formData, payment_provider: e.target.value as 'rollops' | 'self-managed' })}
              className="w-full px-3 py-2 bg-[#363232] border border-[#504A4A] rounded-lg text-[#EBFFEE] focus:outline-none focus:ring-2 focus:ring-[#9B1421]"
            >
              <option value="rollops">RollOps (Automated)</option>
              <option value="self-managed">Self-Managed</option>
            </select>
          </div>

          <div>
            <label className="block text-sm text-[#A9A3A3] mb-2">Billing Strategy</label>
            <select
              value={formData.billing_strategy}
              onChange={(e) => onFormChange({ ...formData, billing_strategy: e.target.value as 'monthly_fixed_date' | 'on_signup' })}
              className="w-full px-3 py-2 bg-[#363232] border border-[#504A4A] rounded-lg text-[#EBFFEE] focus:outline-none focus:ring-2 focus:ring-[#9B1421]"
            >
              <option value="monthly_fixed_date">Monthly Fixed Date</option>
              <option value="on_signup">On Signup</option>
            </select>
          </div>

          {formData.billing_strategy === 'monthly_fixed_date' && (
            <div>
              <label className="block text-sm text-[#A9A3A3] mb-2">Fixed Billing Day (1-28)</label>
              <input
                type="number"
                min="1"
                max="28"
                value={formData.fixed_billing_day}
                onChange={(e) => onFormChange({ ...formData, fixed_billing_day: parseInt(e.target.value) || 1 })}
                className="w-full px-3 py-2 bg-[#363232] border border-[#504A4A] rounded-lg text-[#EBFFEE] focus:outline-none focus:ring-2 focus:ring-[#9B1421]"
              />
            </div>
          )}
        </div>

        <div className="flex gap-3 mt-6">
          <Button
            variant="red"
            onClick={onSave}
            className="flex-1"
          >
            Save
          </Button>
          <Button
            variant="outline"
            onClick={onClose}
            className="flex-1 border-[#504A4A] text-[#EBFFEE] hover:bg-[#363232]"
          >
            Cancel
          </Button>
        </div>
      </div>
    </div>
  );
}

export default function CustomerDashboard() {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [companies, setCompanies] = useState<Company[]>([]);
  const [filteredCompanies, setFilteredCompanies] = useState<Company[]>([]);
  const [selectedCompany, setSelectedCompany] = useState<Company | null>(null);
  const [companyLocations, setCompanyLocations] = useState<CompanyLocation[]>([]);
  const [loading, setLoading] = useState(true);
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const [expandedCompanies, setExpandedCompanies] = useState<Set<string>>(new Set());
  const [showInternal, setShowInternal] = useState(false);
  const [editingBillingSettings, setEditingBillingSettings] = useState<Company | null>(null);
  const [billingSettingsForm, setBillingSettingsForm] = useState({
    payment_provider: 'rollops' as 'rollops' | 'self-managed',
    billing_strategy: 'monthly_fixed_date' as 'monthly_fixed_date' | 'on_signup',
    fixed_billing_day: 1,
  });

  // Use TanStack Query hooks for company users and draft students
  const companyUsersQuery = useAdminCompanyUsers(selectedCompany?.id || null);
  const draftStudentsQuery = useAdminDraftStudents(selectedCompany?.id || null);
  const updateRoleMutation = useUpdateUserRole();
  const systemRolesQuery = useSystemRoles();

  const applyFilters = useCallback((companyList: Company[], query: string, includeInternal: boolean) => {
    const normalizedQuery = query.trim().toLowerCase();
    const base = includeInternal ? companyList : companyList.filter((c) => !c.is_internal);

    if (!normalizedQuery) {
      return base;
    }

    return base.filter((company) => {
      const name = company.name?.toLowerCase() || '';
      const email = company.company_email?.toLowerCase() || '';
      const city = company.city?.toLowerCase() || '';
      const state = company.state?.toLowerCase() || '';
      const contactName = `${company.contact_fn || ''} ${company.contact_ln || ''}`.toLowerCase();

      return (
        name.includes(normalizedQuery) ||
        email.includes(normalizedQuery) ||
        city.includes(normalizedQuery) ||
        state.includes(normalizedQuery) ||
        contactName.includes(normalizedQuery)
      );
    });
  }, []);

  const loadCompanies = useCallback(async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('companies')
        .select(`
          *,
          subscription_billing_settings (
            payment_provider
          )
        `)
        .order('created_at', { ascending: false });

      if (error) throw error;

      // Map data and add default is_internal if not present, and determine billing status
      const companyRows = (data || []) as unknown as Array<Company & {
        subscription_billing_settings?: Array<{
          payment_provider: string;
        }> | null;
      }>;

      const mappedData = companyRows.map((company) => {
        const billingSettings = Array.isArray(company.subscription_billing_settings)
          ? company.subscription_billing_settings[0]
          : company.subscription_billing_settings || null;
        let billingStatus: 'rollops' | 'self_managed' | 'not_configured' = 'not_configured';

        if (billingSettings) {
          if (billingSettings.payment_provider === 'self-managed') {
            billingStatus = 'self_managed';
          } else if (billingSettings.payment_provider === 'rollops') {
            billingStatus = 'rollops';
          }
        }

        return {
          ...company,
          is_internal: company.is_internal ?? false,
          enable_bulk_draft_updates: company.enable_bulk_draft_updates ?? false,
          enable_billing: company.enable_billing ?? true, // Default to true for backwards compatibility
          billing_status: billingStatus,
        };
      });
      setCompanies(mappedData);
      setFilteredCompanies(applyFilters(mappedData, searchQuery, showInternal));
    } catch {
      // Error loading companies - state will show loading=false
    } finally {
      setLoading(false);
    }
  }, [searchQuery, showInternal, applyFilters]);

  useEffect(() => {
    loadCompanies();
  }, [loadCompanies]);

  useEffect(() => {
    setFilteredCompanies(applyFilters(companies, searchQuery, showInternal));
  }, [searchQuery, companies, showInternal, applyFilters]);

  const loadCompanyDetails = async (companyId: string) => {
    try {
      // Load locations (still using direct query since it's simple)
      const { data: locationsData, error: locationsError } = await supabase
        .from('locations')
        .select('id, name, address, city, state')
        .eq('company_id', companyId);

      if (locationsError) throw locationsError;
      setCompanyLocations(locationsData || []);
    } catch (error) {
      setCompanyLocations([]);
    }
  };

  const toggleCompanyExpansion = (companyId: string) => {
    const newExpanded = new Set(expandedCompanies);
    if (newExpanded.has(companyId)) {
      newExpanded.delete(companyId);
      setSelectedCompany(null);
      setCompanyLocations([]);
    } else {
      newExpanded.add(companyId);
      const company = companies.find(c => c.id === companyId);
      if (company) {
        setSelectedCompany(company);
        loadCompanyDetails(companyId);
      }
    }
    setExpandedCompanies(newExpanded);
  };

  const handleEmailOwner = (company: Company) => {
    const ownerEmail = company.company_email || `${company.contact_fn || ''}.${company.contact_ln || ''}@rollops.pro`.toLowerCase();
    window.location.href = `mailto:${ownerEmail}?subject=RollOps Account Inquiry`;
  };

  const handleToggleInternal = async (companyId: string, currentValue: boolean) => {
    try {
      // Note: This requires is_internal column to exist in companies table
      // Migration needed: ALTER TABLE companies ADD COLUMN IF NOT EXISTS is_internal boolean DEFAULT false;
      const { error } = await supabase
        .from('companies')
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        .update({ is_internal: !currentValue } as any)
        .eq('id', companyId);

      if (error) {
        alert('Feature not available yet. Migration needed to add is_internal column.');
        return;
      }

      // Update local state
      setCompanies(companies.map(c => 
        c.id === companyId ? { ...c, is_internal: !currentValue } as Company : c
      ));
      setFilteredCompanies(filteredCompanies.map(c => 
        c.id === companyId ? { ...c, is_internal: !currentValue } as Company : c
      ));
      if (selectedCompany?.id === companyId) {
        setSelectedCompany({ ...selectedCompany, is_internal: !currentValue } as Company);
      }
    } catch (error) {
      alert('Failed to update internal flag');
    }
  };

  const handleToggleBulkDraftUpdates = async (companyId: string, currentValue: boolean) => {
    try {
      // Use admin_update_feature_toggles function
      // Type assertion needed until TypeScript types are regenerated
      // Type assertion needed because admin_update_feature_toggles is not in generated types
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const { error } = await (supabase.rpc as any)('admin_update_feature_toggles', {
        p_company_id: companyId,
        p_enable_bulk_draft_updates: !currentValue
      });

      if (error) {
        alert('Failed to update bulk draft updates toggle. Make sure you have admin access.');
        return;
      }

      // Update local state
      const newValue = !currentValue;
      setCompanies(companies.map(c => 
        c.id === companyId ? { ...c, enable_bulk_draft_updates: newValue } as Company : c
      ));
      setFilteredCompanies(filteredCompanies.map(c => 
        c.id === companyId ? { ...c, enable_bulk_draft_updates: newValue } as Company : c
      ));
      if (selectedCompany?.id === companyId) {
        setSelectedCompany({ ...selectedCompany, enable_bulk_draft_updates: newValue } as Company);
      }
    } catch (error) {
      console.error('[CustomerDashboard] Error toggling bulk draft updates:', error);
      alert('Failed to update bulk draft updates toggle');
    }
  };

  const handleToggleBilling = async (companyId: string, currentValue: boolean) => {
    try {
      // Use admin_update_feature_toggles function
      // Type assertion needed until TypeScript types are regenerated
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const { error } = await (supabase.rpc as any)('admin_update_feature_toggles', {
        p_company_id: companyId,
        p_enable_billing: !currentValue
      });

      if (error) {
        alert('Failed to update billing toggle. Make sure you have admin access.');
        return;
      }

      // Update local state
      const newValue = !currentValue;
      setCompanies(companies.map(c => 
        c.id === companyId ? { ...c, enable_billing: newValue } as Company : c
      ));
      setFilteredCompanies(filteredCompanies.map(c => 
        c.id === companyId ? { ...c, enable_billing: newValue } as Company : c
      ));
      if (selectedCompany?.id === companyId) {
        setSelectedCompany({ ...selectedCompany, enable_billing: newValue } as Company);
      }
    } catch (error) {
      console.error('[CustomerDashboard] Error toggling billing:', error);
      alert('Failed to update billing toggle');
    }
  };

  const handleToggleActive = async (companyId: string, currentValue: boolean) => {
    try {
      const { error } = await supabase
        .from('companies')
        .update({ is_active: !currentValue })
        .eq('id', companyId);

      if (error) throw error;

      // Update local state
      setCompanies(companies.map(c => 
        c.id === companyId ? { ...c, is_active: !currentValue } : c
      ));
      setFilteredCompanies(filteredCompanies.map(c => 
        c.id === companyId ? { ...c, is_active: !currentValue } : c
      ));
      if (selectedCompany?.id === companyId) {
        setSelectedCompany({ ...selectedCompany, is_active: !currentValue });
      }
    } catch (error) {
      alert('Failed to update active flag');
    }
  };

  const handleEditBillingSettings = async (company: Company) => {
    // Load current billing settings
    const { data: settings } = await supabase
      .from('subscription_billing_settings')
      .select('payment_provider, billing_strategy, fixed_billing_day')
      .eq('company_id', company.id)
      .maybeSingle();

    if (settings) {
      setBillingSettingsForm({
        payment_provider: (settings.payment_provider as 'rollops' | 'self-managed') || 'rollops',
        billing_strategy: (settings.billing_strategy as 'monthly_fixed_date' | 'on_signup') || 'monthly_fixed_date',
        fixed_billing_day: settings.fixed_billing_day || 1,
      });
    } else {
      setBillingSettingsForm({
        payment_provider: 'rollops',
        billing_strategy: 'monthly_fixed_date',
        fixed_billing_day: 1,
      });
    }
    setEditingBillingSettings(company);
  };

  const handleSaveBillingSettings = async () => {
    if (!editingBillingSettings) return;

    try {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const { error } = await (supabase.from as any)('subscription_billing_settings')
        .upsert({
          company_id: editingBillingSettings.id,
          payment_provider: billingSettingsForm.payment_provider,
          billing_strategy: billingSettingsForm.billing_strategy,
          fixed_billing_day: billingSettingsForm.fixed_billing_day,
        }, { onConflict: 'company_id' });

      if (error) throw error;

      alert('Billing settings updated successfully');
      setEditingBillingSettings(null);
      // Reload companies to refresh data
      loadCompanies();
    } catch (error) {
      console.error('[CustomerDashboard] Error updating billing settings:', error);
      alert(error instanceof Error ? error.message : 'Failed to update billing settings');
    }
  };

  const copyToClipboard = async (text: string, id: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedId(id);
      setTimeout(() => setCopiedId(null), 2000);
    } catch {
      // Clipboard API failed - silently fail as this is a non-critical operation
    }
  };

  const getQRCodeUrl = (companyId: string, locationId?: string) => {
    const baseUrl = typeof window !== 'undefined' ? window.location.origin : 'http://localhost:5173';
    return locationId 
      ? `${baseUrl}/signup/${companyId}/${locationId}`
      : `${baseUrl}/signup/${companyId}`;
  };

  const CopyButton = ({ text, id, label }: { text: string; id: string; label: string }) => (
    <button
      onClick={() => copyToClipboard(text, id)}
      className="flex items-center gap-2 px-3 py-1.5 bg-[#363232] hover:bg-[#504A4A] text-[#EBFFEE] rounded-lg transition-colors text-xs"
    >
      {copiedId === id ? (
        <>
          <Check size={14} className="text-green-500" />
          <span>Copied!</span>
        </>
      ) : (
        <>
          <Copy size={14} />
          <span>{label}</span>
        </>
      )}
    </button>
  );

  const getBillingStatusBadge = (billingStatus?: 'rollops' | 'self_managed' | 'not_configured') => {
    switch (billingStatus) {
      case 'rollops':
        return (
          <span className="px-2 py-0.5 bg-green-600/20 text-green-400 text-xs rounded flex items-center gap-1">
            <DollarSign size={12} />
            Billing RollOps
          </span>
        );
      case 'self_managed':
        return (
          <span className="px-2 py-0.5 bg-purple-600/20 text-purple-400 text-xs rounded flex items-center gap-1">
            <DollarSign size={12} />
            Billing Self Managed
          </span>
        );
      default:
        return null;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <Loader2 size={48} className="animate-spin text-rollops-accent" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-[#EBFFEE] mb-2">Customer Dashboard</h1>
        <p className="text-[#A9A3A3]">Manage all companies, users, and subscriptions</p>
      </div>

      {/* Search Bar */}
      <div className="mb-6">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-[#A9A3A3] w-5 h-5" />
          <input
            type="text"
            placeholder="Search companies by name, email, city, state..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-3 bg-[#363232] border border-[#504A4A] rounded-2xl text-[#EBFFEE] placeholder-[#A9A3A3] focus:outline-none focus:ring-2 focus:ring-[#9B1421]"
          />
        </div>
        <label className="mt-3 flex items-center gap-2 text-sm text-[#A9A3A3]">
          <input
            type="checkbox"
            checked={showInternal}
            onChange={(e) => setShowInternal(e.target.checked)}
            className="rounded border-[#504A4A] bg-[#221F1F]"
          />
          Show internal companies
        </label>
      </div>

      {/* Companies List */}
      <div className="space-y-3">
        {filteredCompanies.map((company) => {
          const isExpanded = expandedCompanies.has(company.id);
          const companyQRUrl = getQRCodeUrl(company.id);

          return (
            <div
              key={company.id}
              className="bg-[#221F1F] border border-[#504A4A] rounded-lg overflow-hidden"
            >
              {/* Company Header */}
              <div
                className="p-4 cursor-pointer hover:bg-[#363232] transition-colors"
                onClick={() => toggleCompanyExpansion(company.id)}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4 flex-1">
                    {isExpanded ? (
                      <ChevronDown className="w-5 h-5 text-[#A9A3A3]" />
                    ) : (
                      <ChevronRight className="w-5 h-5 text-[#A9A3A3]" />
                    )}
                    <div className="flex-1">
                      <div className="flex items-center gap-3">
                        <h3 className="text-lg font-semibold text-[#EBFFEE]">
                          {company.name || 'Unnamed Company'}
                        </h3>
                        {company.is_internal && (
                          <span className="px-2 py-0.5 bg-yellow-600/20 text-yellow-400 text-xs rounded">
                            Internal
                          </span>
                        )}
                        {company.is_active ? (
                          <CheckCircle className="w-5 h-5 text-green-500" />
                        ) : (
                          <XCircle className="w-5 h-5 text-red-500" />
                        )}
                        {company.setup_complete ? (
                          <span className="px-2 py-0.5 bg-green-600/20 text-green-400 text-xs rounded">
                            Setup Complete
                          </span>
                        ) : (
                          <span className="px-2 py-0.5 bg-orange-600/20 text-orange-400 text-xs rounded">
                            Setup Incomplete
                          </span>
                        )}
                        {getBillingStatusBadge(company.billing_status)}
                        {company.enable_bulk_draft_updates && (
                          <span className="px-2 py-0.5 bg-blue-600/20 text-blue-400 text-xs rounded flex items-center gap-1" title="Bulk draft updates enabled">
                            Bulk Updates
                          </span>
                        )}
                      </div>
                      <div className="mt-1 flex items-center gap-4 text-sm text-[#A9A3A3]">
                        {company.company_email && (
                          <span>{company.company_email}</span>
                        )}
                        {(company.city || company.state) && (
                          <span>{[company.city, company.state].filter(Boolean).join(', ')}</span>
                        )}
                        {company.phone && (
                          <span>{company.phone}</span>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleEmailOwner(company);
                      }}
                      className="border-[#504A4A] text-[#EBFFEE] hover:bg-[#363232]"
                    >
                      <Mail size={16} className="mr-2" />
                      Email
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate(`/admin/rollops-subscriptions?companyId=${company.id}`);
                      }}
                      className="border-[#504A4A] text-[#EBFFEE] hover:bg-[#363232]"
                    >
                      <CreditCard size={16} className="mr-2" />
                      Subscriptions
                    </Button>
                  </div>
                </div>
              </div>

              {/* Expanded Details */}
              {isExpanded && (
                <div className="border-t border-[#504A4A] p-4 bg-[#1A1818]">
                  <div className="space-y-6">
                      {/* Quick Actions */}
                      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
                        <Button
                          variant={company.is_active ? "outline" : "red"}
                          size="sm"
                          onClick={() => handleToggleActive(company.id, company.is_active)}
                          className="border-[#504A4A] text-[#EBFFEE] hover:bg-[#363232]"
                        >
                          {company.is_active ? 'Deactivate' : 'Activate'}
                        </Button>
                        <Button
                          variant={company.is_internal ? "outline" : "outline"}
                          size="sm"
                          onClick={() => handleToggleInternal(company.id, Boolean(company.is_internal))}
                          className={`${company.is_internal ? 'bg-yellow-600/20 text-yellow-400 border-yellow-600/50' : 'border-[#504A4A] text-[#EBFFEE]'} hover:bg-[#363232]`}
                        >
                          {company.is_internal ? 'Mark External' : 'Mark Internal'}
                        </Button>
                        <Button
                          variant={company.enable_bulk_draft_updates ? "outline" : "outline"}
                          size="sm"
                          onClick={() => handleToggleBulkDraftUpdates(company.id, Boolean(company.enable_bulk_draft_updates))}
                          className={`${company.enable_bulk_draft_updates ? 'bg-blue-600/20 text-blue-400 border-blue-600/50' : 'border-[#504A4A] text-[#EBFFEE]'} hover:bg-[#363232]`}
                          title="Allows gym owners to update draft student records via re-upload"
                        >
                          {company.enable_bulk_draft_updates ? 'Bulk Updates: ON' : 'Bulk Updates: OFF'}
                        </Button>
                        <Button
                          variant={company.enable_billing ? "outline" : "outline"}
                          size="sm"
                          onClick={() => handleToggleBilling(company.id, Boolean(company.enable_billing))}
                          className={`${company.enable_billing ? 'bg-green-600/20 text-green-400 border-green-600/50' : 'border-[#504A4A] text-[#EBFFEE]'} hover:bg-[#363232]`}
                          title="Enable/disable billing features for this gym"
                        >
                          {company.enable_billing ? 'Billing: ON' : 'Billing: OFF'}
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => navigate(`/dev/companies?companyId=${company.id}`)}
                          className="border-[#504A4A] text-[#EBFFEE] hover:bg-[#363232]"
                        >
                          <Users size={16} className="mr-2" />
                          View Members
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => navigate(`/admin/stripe-management?companyId=${company.id}`)}
                          className="border-[#504A4A] text-[#EBFFEE] hover:bg-[#363232]"
                        >
                          <Settings size={16} className="mr-2" />
                          Settings
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleEditBillingSettings(company)}
                          className="border-[#504A4A] text-[#EBFFEE] hover:bg-[#363232]"
                          title="Edit billing settings (payment provider, billing strategy, etc.)"
                        >
                          <DollarSign size={16} className="mr-2" />
                          Billing Settings
                        </Button>
                      </div>

                      {/* QR Codes Section */}
                      <div className="bg-[#221F1F] rounded-lg p-4 border border-[#504A4A]">
                        <h4 className="text-lg font-semibold text-[#EBFFEE] mb-3 flex items-center gap-2">
                          <QrCode size={20} />
                          QR Code Test Links
                        </h4>
                        <div className="space-y-3">
                          {/* Company QR Code */}
                          <div>
                            <label className="block text-xs text-[#A9A3A3] mb-1">Company QR Code</label>
                            <div className="flex items-center gap-2">
                              <input
                                type="text"
                                value={companyQRUrl}
                                readOnly
                                className="flex-1 px-3 py-2 bg-[#363232] text-[#EBFFEE] rounded-lg border border-[#504A4A] font-mono text-sm"
                              />
                              <CopyButton text={companyQRUrl} id={`company-${company.id}`} label="Copy" />
                              <a
                                href={companyQRUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="px-3 py-2 bg-[#9B1421] hover:bg-[#7A0F1A] text-white rounded-lg transition-colors text-sm flex items-center gap-2"
                              >
                                <ExternalLink size={14} />
                                Test
                              </a>
                            </div>
                          </div>

                          {/* Location QR Codes */}
                          {companyLocations.length > 0 ? (
                            <div>
                              <label className="block text-xs text-[#A9A3A3] mb-2">Location QR Codes</label>
                              <div className="space-y-2">
                                {companyLocations.map((location) => {
                                  const locationQRUrl = getQRCodeUrl(company.id, location.id);
                                  return (
                                    <div key={location.id} className="flex items-center gap-2">
                                      <input
                                        type="text"
                                        value={locationQRUrl}
                                        readOnly
                                        className="flex-1 px-3 py-2 bg-[#363232] text-[#EBFFEE] rounded-lg border border-[#504A4A] font-mono text-sm"
                                      />
                                      <CopyButton text={locationQRUrl} id={`location-${location.id}`} label="Copy" />
                                      <a
                                        href={locationQRUrl}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="px-3 py-2 bg-[#9B1421] hover:bg-[#7A0F1A] text-white rounded-lg transition-colors text-sm flex items-center gap-2"
                                      >
                                        <ExternalLink size={14} />
                                        Test
                                      </a>
                                    </div>
                                  );
                                })}
                              </div>
                            </div>
                          ) : (
                            <p className="text-sm text-[#A9A3A3]">No locations found for this company</p>
                          )}
                        </div>
                      </div>

                      {/* Company Users Section */}
                      <div>
                        <h4 className="text-lg font-semibold text-[#EBFFEE] mb-3 flex items-center gap-2">
                          <Users size={20} />
                          Company Users ({companyUsersQuery.isLoading ? '...' : companyUsersQuery.data?.users.length || 0})
                        </h4>
                        {companyUsersQuery.isLoading ? (
                          <div className="flex items-center justify-center py-8">
                            <Loader2 size={24} className="animate-spin text-rollops-accent" />
                          </div>
                        ) : companyUsersQuery.error ? (
                          <div className="bg-[#221F1F] rounded-lg border border-red-600/50 p-6 text-center">
                            <p className="text-red-400">Error loading users: {companyUsersQuery.error instanceof Error ? companyUsersQuery.error.message : 'Unknown error'}</p>
                          </div>
                        ) : companyUsersQuery.data && companyUsersQuery.data.users.length > 0 ? (
                          <div className="bg-[#221F1F] rounded-lg border border-[#504A4A] overflow-hidden">
                            <table className="w-full">
                              <thead className="bg-[#363232]">
                                <tr>
                                  <th className="px-4 py-2 text-left text-xs font-semibold text-[#EBFFEE]">Name</th>
                                  <th className="px-4 py-2 text-left text-xs font-semibold text-[#EBFFEE]">Email</th>
                                  <th className="px-4 py-2 text-left text-xs font-semibold text-[#EBFFEE]">Role</th>
                                  <th className="px-4 py-2 text-left text-xs font-semibold text-[#EBFFEE]">Status</th>
                                  <th className="px-4 py-2 text-left text-xs font-semibold text-[#EBFFEE]">Joined</th>
                                </tr>
                              </thead>
                              <tbody>
                                {companyUsersQuery.data.users.map((user) => (
                                  <tr key={user.user_id} className="border-t border-[#504A4A] hover:bg-[#363232]">
                                    <td className="px-4 py-2 text-sm text-[#EBFFEE]">
                                      {user.first_name || user.last_name
                                        ? `${user.first_name || ''} ${user.last_name || ''}`.trim()
                                        : 'N/A'}
                                    </td>
                                    <td className="px-4 py-2 text-sm text-[#A9A3A3]">{user.email || 'N/A'}</td>
                                    <td className="px-4 py-2">
                                      {systemRolesQuery.data && selectedCompany ? (
                                        <select
                                          value={user.role}
                                          onChange={async (e) => {
                                            const newRole = e.target.value;
                                            if (newRole === user.role) return;
                                            
                                            try {
                                              await updateRoleMutation.mutateAsync({
                                                userId: user.user_id,
                                                companyId: selectedCompany.id,
                                                newRole,
                                              });
                                              // Query will automatically refetch due to invalidation
                                            } catch (error) {
                                              alert(`Failed to update role: ${error instanceof Error ? error.message : 'Unknown error'}`);
                                            }
                                          }}
                                          disabled={updateRoleMutation.isPending}
                                          className="px-2 py-1 bg-[#363232] text-[#EBFFEE] text-xs rounded capitalize border border-[#504A4A] hover:border-[#9B1421] focus:outline-none focus:ring-2 focus:ring-[#9B1421] cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                                        >
                                          {systemRolesQuery.data.map((role) => (
                                            <option key={role.system_role_id} value={role.role}>
                                              {role.role}
                                            </option>
                                          ))}
                                        </select>
                                      ) : (
                                        <span className="px-2 py-0.5 bg-[#363232] text-[#EBFFEE] text-xs rounded capitalize">
                                          {user.role}
                                        </span>
                                      )}
                                    </td>
                                    <td className="px-4 py-2">
                                      {user.onboarding_complete ? (
                                        <span className="px-2 py-0.5 bg-green-600/20 text-green-400 text-xs rounded flex items-center gap-1 w-fit">
                                          <CheckCircle size={12} />
                                          Complete
                                        </span>
                                      ) : (
                                        <span className="px-2 py-0.5 bg-yellow-600/20 text-yellow-400 text-xs rounded flex items-center gap-1 w-fit">
                                          <XCircle size={12} />
                                          Pending
                                        </span>
                                      )}
                                    </td>
                                    <td className="px-4 py-2 text-sm text-[#A9A3A3]">
                                      {new Date(user.created_at).toLocaleDateString()}
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        ) : (
                          <div className="bg-[#221F1F] rounded-lg border border-[#504A4A] p-6 text-center">
                            <p className="text-[#A9A3A3]">No users found for this company.</p>
                          </div>
                        )}
                      </div>

                      {/* Draft Students Section */}
                      <div>
                        <h4 className="text-lg font-semibold text-[#EBFFEE] mb-3 flex items-center gap-2">
                          <FileText size={20} />
                          Draft Students ({draftStudentsQuery.isLoading ? '...' : draftStudentsQuery.data?.drafts.length || 0})
                        </h4>
                        {draftStudentsQuery.isLoading ? (
                          <div className="flex items-center justify-center py-8">
                            <Loader2 size={24} className="animate-spin text-rollops-accent" />
                          </div>
                        ) : draftStudentsQuery.error ? (
                          <div className="bg-[#221F1F] rounded-lg border border-red-600/50 p-6 text-center">
                            <p className="text-red-400">
                              Error loading draft students: {
                                draftStudentsQuery.error instanceof Error 
                                  ? draftStudentsQuery.error.message 
                                  : (draftStudentsQuery.error as { message?: string; code?: string })?.message || (draftStudentsQuery.error as { message?: string; code?: string })?.code || 'Unknown error'
                              }
                            </p>
                            {process.env.NODE_ENV === 'development' && (
                              <pre className="mt-2 text-xs text-red-300 overflow-auto">
                                {JSON.stringify(draftStudentsQuery.error, null, 2)}
                              </pre>
                            )}
                          </div>
                        ) : draftStudentsQuery.data && draftStudentsQuery.data.drafts.length > 0 ? (
                          <div className="bg-[#221F1F] rounded-lg border border-[#504A4A] overflow-hidden">
                            <table className="w-full">
                              <thead className="bg-[#363232]">
                                <tr>
                                  <th className="px-4 py-2 text-left text-xs font-semibold text-[#EBFFEE]">Name</th>
                                  <th className="px-4 py-2 text-left text-xs font-semibold text-[#EBFFEE]">Email</th>
                                  <th className="px-4 py-2 text-left text-xs font-semibold text-[#EBFFEE]">Phone</th>
                                  <th className="px-4 py-2 text-left text-xs font-semibold text-[#EBFFEE]">Status</th>
                                  <th className="px-4 py-2 text-left text-xs font-semibold text-[#EBFFEE]">Created</th>
                                </tr>
                              </thead>
                              <tbody>
                                {draftStudentsQuery.data.drafts.map((draft) => (
                                  <tr key={draft.draft_id} className="border-t border-[#504A4A] hover:bg-[#363232]">
                                    <td className="px-4 py-2 text-sm text-[#EBFFEE]">
                                      {draft.first_name || draft.last_name
                                        ? `${draft.first_name || ''} ${draft.last_name || ''}`.trim()
                                        : 'N/A'}
                                    </td>
                                    <td className="px-4 py-2 text-sm text-[#A9A3A3]">{draft.email || 'N/A'}</td>
                                    <td className="px-4 py-2 text-sm text-[#A9A3A3]">{draft.phone || 'N/A'}</td>
                                    <td className="px-4 py-2">
                                      <span className="px-2 py-0.5 bg-blue-600/20 text-blue-400 text-xs rounded capitalize">
                                        {draft.status || 'pending'}
                                      </span>
                                    </td>
                                    <td className="px-4 py-2 text-sm text-[#A9A3A3]">
                                      {new Date(draft.created_at).toLocaleDateString()}
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        ) : (
                          <div className="bg-[#221F1F] rounded-lg border border-[#504A4A] p-6 text-center">
                            <p className="text-[#A9A3A3]">No draft students found for this company.</p>
                          </div>
                        )}
                      </div>
                    </div>
                </div>
              )}
            </div>
          );
        })}

        {filteredCompanies.length === 0 && (
          <div className="text-center py-12">
            <Building2 size={48} className="mx-auto text-[#A9A3A3] mb-4" />
            <p className="text-[#A9A3A3]">No companies found</p>
          </div>
        )}
      </div>

      {editingBillingSettings && (
        <BillingSettingsModal
          company={editingBillingSettings}
          formData={billingSettingsForm}
          onClose={() => setEditingBillingSettings(null)}
          onSave={handleSaveBillingSettings}
          onFormChange={setBillingSettingsForm}
        />
      )}
    </div>
  );
}
